import {Values} from "@wix/devzai-utils-common";



export const WixEcomCheckoutCustomLineItemKind = {
    PhysicalProduct: 'PhysicalProduct',
    DigitalProduct: 'DigitalProduct',
    GiftCard: 'GiftCard',
    Service: 'Service',
    Custom: 'Custom',
} as const;

export type WixEcomCheckoutCustomLineItemKind = Values<typeof WixEcomCheckoutCustomLineItemKind>;

export type WixEcomCheckoutCustomLineItem =
    | WixEcomCheckoutCustomLineItem.PhysicalProduct
    | WixEcomCheckoutCustomLineItem.DigitalProduct
    | WixEcomCheckoutCustomLineItem.GiftCard
    | WixEcomCheckoutCustomLineItem.Service
    | WixEcomCheckoutCustomLineItem.Custom
;

/**
 * Description line for custom line items - displayed in checkout
 */
export interface WixEcomCheckoutDescriptionLine {
    /** Name/label of the description line (e.g., "Message", "Color") */
    name: string;
    /** Translated name */
    translatedName?: string;
    /** Plain text value */
    plainText?: string;
    /** Translated plain text value */
    translatedPlainText?: string;
    /** Color info (alternative to plainText) */
    colorInfo?: {
        original: string;
        translated?: string;
        code?: string;
    };
}

/**
 * Media for custom line items - displayed in checkout
 */
export interface WixEcomCheckoutLineItemMedia {
    /** URL of the image */
    url: string;
    /** Image width in pixels */
    width?: number;
    /** Image height in pixels */
    height?: number;
}

export namespace WixEcomCheckoutCustomLineItem {

    type BaseType<T extends WixEcomCheckoutCustomLineItemKind> = {
        kind: T;

        /** Quantity of items (1-100000) */
        quantity: number;
        /** Price as decimal string (e.g., "50.00") */
        price: string;
        /** Product/service name displayed in checkout */
        productName: string;
        /** Translated product name */
        translatedProductName?: string;
        
        // Optional display properties available on all types
        /** Description lines shown in checkout (up to 10) - use for messages, notes, etc. */
        descriptionLines?: WixEcomCheckoutDescriptionLine[];
        /** Media/image for the line item */
        media?: WixEcomCheckoutLineItemMedia;
        /** Additional price context (e.g., "Starting at $50") */
        priceDescription?: string;
        /** Translated price description */
        translatedPriceDescription?: string;
        /** Full/original price before discount (shows strikethrough) */
        fullPrice?: string;
    }

    export type PhysicalProduct = BaseType<typeof WixEcomCheckoutCustomLineItemKind.PhysicalProduct> & {
        /** Physical properties for shipping calculation */
        physicalProperties?: {
            weight?: number;
            sku?: string;
            shippable?: boolean;
        };
    }
    export type DigitalProduct = BaseType<typeof WixEcomCheckoutCustomLineItemKind.DigitalProduct> & {}
    export type GiftCard = BaseType<typeof WixEcomCheckoutCustomLineItemKind.GiftCard> & {}
    export type Service = BaseType<typeof WixEcomCheckoutCustomLineItemKind.Service> & {}
    export type Custom = BaseType<typeof WixEcomCheckoutCustomLineItemKind.Custom> & {
        /** Description of the custom item type (shown instead of preset type name) */
        itemTypeDescription?: string;
    }

}

export const WixEcomCheckoutCatalogLineItemKind = {
    WixStoresProduct: 'WixStoresProduct',
    CustomCatalogProduct: 'CustomCatalogProduct',
} as const;

export type WixEcomCheckoutCatalogLineItemKind = Values<typeof WixEcomCheckoutCatalogLineItemKind>;

export type WixEcomCheckoutCatalogLineItem =
    | WixEcomCheckoutCatalogLineItem.WixStoresProduct
    | WixEcomCheckoutCatalogLineItem.CustomCatalogProduct
;

export namespace WixEcomCheckoutCatalogLineItem {

    type BaseType<T extends WixEcomCheckoutCatalogLineItemKind> = {
        kind: T;
        quantity: number;
    }

    export type WixStoresProduct = BaseType<typeof WixEcomCheckoutCatalogLineItemKind.WixStoresProduct> & {
        productId: string;
        /**
         * Variant ID for products with managed variants (manageVariants: true).
         * For non-managed variants, pass empty string and use productOptions instead.
         */
        variantId: string;
        /**
         * Product options for non-managed variants (manageVariants: false).
         * Pass the selected options as key-value pairs, e.g., { "Color": "Black", "Size": "250ml" }
         */
        productOptions?: Record<string, string>;
        modifiersChoices?: Record<string, any>;
        textFieldsUserInput?: Record<string, string>;
        subscriptionOptionId?: string;
    }

    export type CustomCatalogProduct = BaseType<typeof WixEcomCheckoutCatalogLineItemKind.CustomCatalogProduct> & {
        catalogAppId: string;
        catalogItemId: string;
        catalogItemOptions?: Record<string, any>;
    }
}

export type WixEcomCheckoutLineItem = {
    quantity: number;
    catalogAppId: string;
    catalogItemId: string;
    catalogItemOptions?: Record<string, any>;
}